<template>
  <main class="w-full">
    <div class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-2xl lg:text-3xl font-bold text-foreground">Catégories</h1>
      <p class="mb-2 text-muted-foreground mt-1">Organisez vos produits par catégories</p>
      <div class="flex justify-end mb-6">
        <button @click="openModal" class="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-2 px-4 rounded-md transition-colors flex items-center">
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Nouvelle catégorie
        </button>
      </div>
    </div>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <svg class="animate-spin h-8 w-8 text-primary mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <p class="text-muted-foreground">Chargement des catégories...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="categories.length === 0" class="bg-card border border-border rounded-lg shadow-md">
        <div class="p-12 text-center">
          <svg class="h-16 w-16 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <h3 class="text-xl font-semibold mb-2">
            Aucune catégorie
          </h3>
          <p class="text-muted-foreground mb-6">
            Commencez par créer votre première catégorie
          </p>
          <button @click="openModal" class="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-2 px-4 rounded-md transition-colors">
            Créer une catégorie
          </button>
        </div>
      </div>

      <!-- Categories Grid -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div v-for="category in categories" :key="category.id" class="bg-card border border-border rounded-lg shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
          <div class="p-5">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center space-x-3">
                <span class="text-3xl">{{ category.icon || '📁' }}</span>
                <div>
                  <h3 class="font-semibold">{{ category.name }}</h3>
                  <p class="text-xs text-muted-foreground">/{{ category.slug }}</p>
                </div>
              </div>
              <span :class="`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`">
                {{ category.is_active ? 'Actif' : 'Inactif' }}
              </span>
            </div>

            <p class="text-sm text-muted-foreground mb-4 line-clamp-2">
              {{ category.description || 'Aucune description' }}
            </p>

            <div class="flex items-center justify-between pt-3 border-t border-border">
              <div class="flex items-center space-x-2 text-sm">
                <svg class="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                <span class="font-medium">{{ category.productCount || 0 }}</span>
                <span class="text-muted-foreground">produits</span>
              </div>

              <div class="flex space-x-1">
                <button @click="editCategory(category)" class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8 rounded-md transition-colors flex items-center justify-center">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <button @click="showDeleteModal(category.id, category.name)" class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8 rounded-md transition-colors flex items-center justify-center text-destructive hover:text-destructive/80">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-card border border-border rounded-lg shadow-md w-full max-w-md">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold">
              {{ editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie' }}
            </h3>
            <button @click="closeModal" class="text-foreground hover:text-secondary transition-colors p-1">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Icône (emoji)</label>
              <input 
                v-model="categoryForm.icon" 
                @input="categoryForm.icon = $event.target.value"
                placeholder="👟"
                class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-2xl"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Nom *</label>
              <input 
                v-model="categoryForm.name" 
                @input="handleNameChange"
                required 
                class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Slug *</label>
              <input 
                v-model="categoryForm.slug" 
                @input="categoryForm.slug = $event.target.value"
                required 
                class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Description</label>
              <textarea 
                v-model="categoryForm.description" 
                @input="categoryForm.description = $event.target.value"
                rows="3" 
                class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Statut</label>
              <div class="flex space-x-4">
                <label class="inline-flex items-center">
                  <input 
                    type="radio" 
                    v-model="categoryForm.is_active" 
                    :value="1"
                    class="form-radio text-primary"
                  />
                  <span class="ml-2">Actif</span>
                </label>
                <label class="inline-flex items-center">
                  <input 
                    type="radio" 
                    v-model="categoryForm.is_active" 
                    :value="0"
                    class="form-radio text-primary"
                  />
                  <span class="ml-2">Inactif</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-foreground mb-2">Image (optionnel)</label>
              <div class="flex items-center space-x-4">
                <input 
                  type="file" 
                  ref="fileInput"
                  @change="handleFileChange"
                  accept="image/*"
                  class="hidden"
                />
                <button 
                  type="button" 
                  @click="$refs.fileInput.click()"
                  class="border border-input bg-background hover:bg-accent hover:text-accent-foreground py-2 px-4 rounded-md transition-colors"
                >
                  {{ categoryForm.imageFile ? 'Changer' : 'Choisir une image' }}
                </button>
                <span v-if="categoryForm.imageFile" class="text-sm text-muted-foreground">
                  {{ categoryForm.imageFile.name }}
                </span>
                <span v-else-if="categoryForm.image" class="text-sm text-muted-foreground">
                  Image actuelle
                </span>
              </div>
            </div>

            <div v-if="error" class="bg-red-50 text-red-800 p-3 rounded-md text-sm">
              {{ error }}
            </div>

            <div class="flex space-x-3 pt-4">
              <button 
                type="button" 
                @click="closeModal" 
                :disabled="saving"
                class="flex-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button 
                type="submit" 
                :disabled="saving"
                class="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="saving" class="flex items-center justify-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  {{ editingCategory ? 'Enregistrement...' : 'Ajout...' }}
                </span>
                <span v-else>
                  {{ editingCategory ? 'Enregistrer' : 'Ajouter' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="deleteModalData" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-card border border-border rounded-lg shadow-md w-full max-w-sm">
        <div class="p-6 text-center">
          <svg class="h-12 w-12 text-destructive mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
          </svg>
          <h3 class="text-lg font-semibold mb-2">Confirmer la suppression</h3>
          <p class="text-muted-foreground mb-6">Supprimer "{{ deleteModalData.name }}" ?</p>
          <div v-if="deleteError" class="bg-red-50 text-red-800 p-3 rounded-md text-sm mb-4">
            {{ deleteError }}
          </div>
          <div class="flex space-x-3">
            <button 
              @click="deleteModalData = null" 
              :disabled="deleting"
              class="flex-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button 
              @click="handleDelete" 
              :disabled="deleting"
              class="flex-1 bg-destructive hover:bg-destructive/90 text-white font-semibold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="deleting" class="flex items-center justify-center">
                <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Suppression...
              </span>
              <span v-else>Supprimer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'AdminCategories',
  
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      showModal: false,
      deleteModalData: null,
      editingCategory: null,
      error: null,
      deleteError: null,
      
      categoryForm: {
        id: null,
        name: '',
        slug: '',
        description: '',
        icon: '',
        is_active: 1,
        image: null,
        imageFile: null
      }
    }
  },
  
  computed: {
    categories() {
      // Utilisez les catégories admin du store
      return this.$store.state.admin.categories
    },
    
    // Ou utilisez le getter
    adminCategories() {
      return this.$store.getters.adminCategories
    }
  },
  
  async mounted() {
    await this.loadCategories()
  },
  
  methods: {
    async loadCategories() {
      try {
        this.loading = true
        // Chargez les données admin depuis le store
        await this.$store.dispatch('fetchAdminData')
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error)
        this.error = 'Impossible de charger les catégories. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },
    
    openModal() {
      this.showModal = true
      this.error = null
    },
    
    closeModal() {
      this.showModal = false
      this.editingCategory = null
      this.categoryForm = { 
        id: null,
        name: '', 
        slug: '', 
        description: '', 
        icon: '',
        is_active: 1,
        image: null,
        imageFile: null 
      }
      this.error = null
    },
    
    async handleSubmit() {
      try {
        this.saving = true
        this.error = null
        
        // Préparez les données pour l'API
        const formData = new FormData()
        
        // Ajoutez tous les champs sauf les fichiers
        Object.keys(this.categoryForm).forEach(key => {
          if (key !== 'imageFile') {
            if (this.categoryForm[key] !== null && this.categoryForm[key] !== undefined) {
              formData.append(key, this.categoryForm[key])
            }
          }
        })
        
        // Ajoutez le fichier image s'il y en a un
        if (this.categoryForm.imageFile instanceof File) {
          formData.append('image', this.categoryForm.imageFile)
        }
        
        // Appelez l'action du store
        const savedCategory = await this.$store.dispatch('saveCategory', formData)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: `Catégorie ${this.editingCategory ? 'modifiée' : 'créée'} avec succès`,
          type: 'success'
        })
        
        this.closeModal()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        this.error = error.response?.data?.message || 'Une erreur est survenue lors de la sauvegarde.'
      } finally {
        this.saving = false
      }
    },
    
    editCategory(category) {
      this.editingCategory = category
      this.categoryForm = {
        id: category.id,
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        icon: category.icon || '',
        is_active: category.is_active !== undefined ? category.is_active : 1,
        image: category.image || null,
        imageFile: null
      }
      this.showModal = true
    },
    
    showDeleteModal(id, name) {
      this.deleteModalData = { id, name }
      this.deleteError = null
    },
    notify(title,message,type){
          this.$emit('notify',{title:title,message:message,type:type})
    },
    async handleDelete() {
      try {
        this.deleting = true
        this.deleteError = null
        
        // Appelez l'action de suppression du store
        await this.$store.dispatch('deleteCategory', this.deleteModalData.id)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: 'Catégorie supprimée avec succès',
          type: 'success'
        })
        
        this.deleteModalData = null
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        this.deleteError = error.response?.data?.message || 'Une erreur est survenue lors de la suppression.'
      } finally {
        this.deleting = false
      }
    },
    
    handleNameChange(event) {
      const name = event.target.value
      this.categoryForm.name = name
      if (!this.editingCategory) {
        this.categoryForm.slug = this.generateSlug(name)
      }
    },
    
    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.categoryForm.imageFile = file
      }
    },
    
    generateSlug(name) {
  return name
    .toLowerCase()
    // Autorise lettres (avec accents), chiffres et tirets
    .replace(/[^a-z0-9àâäéèêëîïôöùûüç-]+/gi, '-')
    // Regroupe tous les tirets multiples en un seul
    .replace(/-+/g, '-')
    // Supprime les tirets au début et à la fin
    .replace(/^-+|-+$/g, '');
}
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>